import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  CheckCircle, Zap, Star, Crown, ArrowRight, Loader2, AlertCircle,
  XCircle, RefreshCw, Calendar, CreditCard, TrendingUp,
  AlertTriangle, Shield, Activity, Rocket
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = iso => iso ? new Date(iso).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';
const fmtCurrency = (amount, currency = 'EUR') =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency, minimumFractionDigits: 0 }).format(amount);

// Feature label map
const FEATURE_LABELS = {
  active_briefs: 'Aktif Brief',
  proposal_views: 'Teklif Görüntüleme',
  analytics: 'Analitik',
  proposals_per_month: 'Aylık Teklif',
  premium_listings: 'Öne Çıkan Listeler',
  api_access: 'API Erişimi',
  custom_branding: 'Özel Marka',
  team_members: 'Ekip Üyeleri',
  brief_boost_credits: 'Brief Boost Kredisi',
  featured_placement: 'Öne Çıkarma',
  priority_support: 'Öncelikli Destek',
  white_label: 'White Label',
};

const FeatureRow = ({ featureKey, value }) => {
  const label = FEATURE_LABELS[featureKey] || featureKey.replace(/_/g, ' ');
  const display = value === true ? '✓' : value === false ? '✗' : value === -1 ? 'Sınırsız' : value;
  const isDisabled = value === false;
  return (
    <li className={`flex items-center gap-2 text-sm ${isDisabled ? 'text-gray-300' : 'text-gray-600'}`}>
      <CheckCircle className={`h-4 w-4 shrink-0 ${isDisabled ? 'text-gray-200' : 'text-green-500'}`} />
      <span className={isDisabled ? 'line-through' : ''}>
        {label}{typeof value !== 'boolean' ? `: ${display}` : ''}
      </span>
    </li>
  );
};

// ── Usage Progress Bar ────────────────────────────────────────────────────────
const UsageBar = ({ item, onUpgrade }) => {
  const pct = item.unlimited ? 0 : Math.min(100, Math.round((item.current / item.limit) * 100));
  const isAtLimit = !item.unlimited && item.current >= item.limit;
  const isNearLimit = !item.unlimited && pct >= 80 && !isAtLimit;
  const barColor = isAtLimit ? 'bg-red-500' : isNearLimit ? 'bg-amber-500' : 'bg-green-500';
  const textColor = isAtLimit ? 'text-red-700' : isNearLimit ? 'text-amber-700' : 'text-gray-700';
  const bgColor = isAtLimit ? 'bg-red-50 border-red-200' : isNearLimit ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200';
  return (
    <div className={`rounded-xl border p-4 ${bgColor}`} data-testid={`usage-bar-${item.key}`}>
      <div className="flex items-center justify-between mb-2">
        <span className={`text-sm font-semibold ${textColor}`}>{item.label}</span>
        {item.unlimited ? (
          <span className="text-xs font-bold text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full">Sınırsız</span>
        ) : (
          <span className={`text-sm font-bold ${textColor}`}>{item.current}/{item.limit} {item.unit}</span>
        )}
      </div>
      {!item.unlimited && (
        <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
          <div className={`h-2 rounded-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
        </div>
      )}
      {isAtLimit && (
        <div className="flex items-center justify-between mt-2">
          <p className="text-xs text-red-600 font-medium">Limit doldu! Yeni {item.unit} oluşturamazsınız.</p>
          <button onClick={onUpgrade} className="text-xs font-bold text-white bg-red-600 hover:bg-red-700 px-3 py-1 rounded-lg flex items-center gap-1">
            <Rocket className="h-3 w-3" /> Yükselt
          </button>
        </div>
      )}
      {isNearLimit && (
        <p className="text-xs text-amber-700 font-medium mt-1 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" /> Limitinizin %{pct}'ine ulaştınız.
        </p>
      )}
    </div>
  );
};

const UsagePanel = ({ token, onUpgradeClick }) => {
  const [usage, setUsage] = useState(null);
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/billing/usage`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setUsage(r.data)).catch(() => {});
  }, [token]);
  if (!usage || !usage.usage?.length) return null;
  const hasAtLimit = usage.usage.some(u => !u.unlimited && u.current >= u.limit);
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5" data-testid="usage-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-blue-500" />
          <h3 className="text-sm font-bold text-gray-800">Kullanım İstatistikleri</h3>
          <Badge className="bg-gray-100 text-gray-600 border-0 text-xs">{usage.plan_name} Planı</Badge>
        </div>
        {hasAtLimit && (
          <button onClick={onUpgradeClick}
            className="text-xs font-bold text-white bg-gradient-to-r from-orange-500 to-orange-600 px-3 py-1.5 rounded-lg flex items-center gap-1.5 shadow-sm hover:shadow-md transition-all">
            <Rocket className="h-3.5 w-3.5" /> Planı Yükselt
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {usage.usage.map(item => (
          <UsageBar key={item.key} item={item} onUpgrade={onUpgradeClick} />
        ))}
      </div>
    </div>
  );
};


// ── Cancel Confirmation Modal ─────────────────────────────────────────────────
const CancelModal = ({ sub, onConfirm, onClose, loading }) => (
  <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-3 rounded-full bg-red-100">
          <XCircle className="h-5 w-5 text-red-600" />
        </div>
        <div>
          <h3 className="font-bold text-gray-900">Aboneliği İptal Et</h3>
          <p className="text-sm text-gray-500">Bu işlem geri alınabilir.</p>
        </div>
      </div>
      <p className="text-sm text-gray-700 mb-2">
        <strong>{sub?.plan_name}</strong> aboneliğinizi iptal etmek istediğinizden emin misiniz?
      </p>
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-5">
        <p className="text-xs text-amber-800 font-medium">
          Aboneliğiniz <strong>{fmtDate(sub?.current_period_end)}</strong> tarihine kadar aktif kalacak.
          Bu tarihten sonra ücretsiz plana geçilecek.
        </p>
      </div>
      <div className="flex gap-3">
        <Button variant="outline" onClick={onClose} className="flex-1" disabled={loading}>
          Vazgeç
        </Button>
        <Button
          onClick={onConfirm}
          disabled={loading}
          className="flex-1 bg-red-600 hover:bg-red-700"
          data-testid="cancel-sub-confirm-btn"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'İptal Et'}
        </Button>
      </div>
    </div>
  </div>
);

// ── Subscription Status Panel ─────────────────────────────────────────────────
const SubscriptionStatusPanel = ({ sub, onCancel, onReactivate, actionLoading }) => {
  const isCancelling = sub.cancel_at_period_end;
  const isPaid = sub.price_month > 0;

  return (
    <div className={`rounded-2xl p-5 border-2 ${isCancelling ? 'border-amber-300 bg-amber-50' : 'border-green-300 bg-green-50'}`}
      data-testid="subscription-status-panel">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className={`p-2.5 rounded-full ${isCancelling ? 'bg-amber-100' : 'bg-green-100'}`}>
            <Shield className={`h-5 w-5 ${isCancelling ? 'text-amber-600' : 'text-green-600'}`} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="font-bold text-gray-900 text-base">{sub.plan_name} Planı</p>
              <Badge className={`text-xs border-0 ${isCancelling ? 'bg-amber-200 text-amber-800' : 'bg-green-200 text-green-800'}`}>
                {isCancelling ? 'İptal Bekliyor' : 'Aktif'}
              </Badge>
            </div>
            {isPaid && sub.current_period_end && (
              <div className="flex items-center gap-1.5 mt-1.5 text-sm text-gray-600">
                <Calendar className="h-3.5 w-3.5 text-gray-400" />
                {isCancelling ? (
                  <span className="text-amber-700 font-medium">
                    {fmtDate(sub.current_period_end)} tarihinde sona eriyor
                  </span>
                ) : (
                  <span>Sonraki yenileme: <strong>{fmtDate(sub.current_period_end)}</strong></span>
                )}
              </div>
            )}
            {isPaid && (
              <div className="flex items-center gap-1.5 mt-1 text-sm text-gray-600">
                <CreditCard className="h-3.5 w-3.5 text-gray-400" />
                {fmtCurrency(sub.price_month, sub.currency)}/ay &nbsp;
                <span className="text-gray-400">• {sub.billing_cycle === 'year' ? 'Yıllık' : 'Aylık'} faturalama</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-2 shrink-0">
          {isPaid && !isCancelling && (
            <Button
              variant="outline"
              size="sm"
              onClick={onCancel}
              disabled={actionLoading}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 text-xs gap-1.5"
              data-testid="cancel-sub-btn"
            >
              <XCircle className="h-3.5 w-3.5" />
              Aboneliği İptal Et
            </Button>
          )}
          {isPaid && isCancelling && (
            <Button
              size="sm"
              onClick={onReactivate}
              disabled={actionLoading}
              className="bg-green-600 hover:bg-green-700 text-xs gap-1.5"
              data-testid="reactivate-sub-btn"
            >
              {actionLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              Yeniden Aktif Et
            </Button>
          )}
        </div>
      </div>

      {isCancelling && (
        <div className="mt-3 pt-3 border-t border-amber-200">
          <p className="text-xs text-amber-700">
            Aboneliğiniz iptal edildi ancak <strong>{fmtDate(sub.current_period_end)}</strong> tarihine kadar tüm özelliklere erişiminiz devam eder.
            &nbsp;<button onClick={onReactivate} className="underline font-medium">Yeniden aktif et →</button>
          </p>
        </div>
      )}
    </div>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────────
export default function BillingPage({ role }) {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const cancelled = searchParams.get('cancelled');

  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [interval, setIntervalState] = useState('month');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [toast, setToast] = useState(null);

  const orgId = user?.org_memberships?.[0]?.org_id || '';

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Poll payment status after Stripe redirect
  const pollStatus = useCallback(async (sid) => {
    if (!sid || !token) return;
    try {
      const res = await axios.get(`${API}/billing/status/${sid}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.payment_status === 'paid') {
        setPaymentStatus('success');
        navigate(window.location.pathname, { replace: true });
        // Refresh subscription
        const subRes = await axios.get(`${API}/billing/subscription`, { headers: { Authorization: `Bearer ${token}` } });
        if (subRes.data?.plan_code) setSubscription(subRes.data);
      } else {
        setTimeout(() => pollStatus(sid), 2000);
      }
    } catch {
      setTimeout(() => pollStatus(sid), 3000);
    }
  }, [token, navigate]);

  useEffect(() => {
    if (sessionId) pollStatus(sessionId);
    if (cancelled) setPaymentStatus('cancelled');
  }, [sessionId, cancelled, pollStatus]);

  useEffect(() => {
    const load = async () => {
      if (!token) return;
      try {
        const [plansRes, subRes] = await Promise.allSettled([
          axios.get(`${API}/ultra/public-plans/${role.toUpperCase()}`),
          axios.get(`${API}/billing/subscription`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        if (plansRes.status === 'fulfilled') {
          setPlans(plansRes.value.data?.items || []);
        }
        if (subRes.status === 'fulfilled' && subRes.value.data?.plan_code) {
          setSubscription(subRes.value.data);
        }
      } catch { /* ignore */ } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, role]);

  const handleSubscribe = async (plan) => {
    if (!token || !orgId) return;
    setCheckoutLoading(plan.code);
    try {
      const origin = window.location.origin;
      // Use upgrade if already has subscription, otherwise subscribe
      const endpoint = subscription?.stripe_subscription_id ? '/billing/subscription/upgrade' : '/billing/subscribe';
      const res = await axios.post(
        `${API}${endpoint}`,
        { org_id: orgId, plan_id: plan.id, plan_code: plan.code, interval, origin_url: origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const url = res.data.checkout_url;
      if (url) {
        if (url.includes('?plan=free')) {
          setPaymentStatus('success');
          setSubscription({ ...subscription, plan_code: plan.code, plan_name: plan.name, price_month: 0 });
          showToast('Planınız başarıyla değiştirildi.');
        } else if (res.data.upgraded) {
          showToast(res.data.message || 'Planınız yükseltildi.');
          const subRes = await axios.get(`${API}/billing/subscription`, { headers: { Authorization: `Bearer ${token}` } });
          if (subRes.data?.plan_code) setSubscription(subRes.data);
        } else {
          window.location.href = url;
        }
      }
    } catch (err) {
      showToast(err?.response?.data?.detail || 'İşlem başarısız. Lütfen tekrar deneyin.', 'error');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleCancel = async () => {
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/billing/subscription/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscription(prev => ({
        ...prev,
        cancel_at_period_end: true,
        current_period_end: res.data.current_period_end || prev?.current_period_end,
      }));
      setShowCancelModal(false);
      showToast('Aboneliğiniz dönem sonunda iptal edilecek.');
    } catch (err) {
      showToast(err?.response?.data?.detail || 'İptal başarısız.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/billing/subscription/reactivate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscription(prev => ({ ...prev, cancel_at_period_end: false }));
      showToast('Aboneliğiniz yeniden aktif edildi!');
    } catch (err) {
      showToast(err?.response?.data?.detail || 'Yeniden aktivasyon başarısız.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="animate-spin h-8 w-8 text-gray-400" />
    </div>
  );

  const currentPlanCode = subscription?.plan_code;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-6 right-6 z-50 px-5 py-3 rounded-xl shadow-xl text-sm font-medium flex items-center gap-2 ${
          toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-gray-900 text-white'
        }`}>
          {toast.type === 'error' ? <XCircle className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Faturalama & Planlar</h1>
        <p className="text-sm text-gray-500 mt-1">İhtiyacınıza uygun planı seçin</p>
      </div>

      {/* Payment status banners */}
      {paymentStatus === 'success' && (
        <div data-testid="billing-success-banner"
          className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl p-4">
          <CheckCircle className="h-5 w-5 text-green-600 shrink-0" />
          <div>
            <p className="text-sm font-bold text-green-800">Ödeme başarılı!</p>
            <p className="text-xs text-green-700 mt-0.5">Planınız aktif edildi. Yeni özelliklerinizin keyfini çıkarın!</p>
          </div>
        </div>
      )}
      {paymentStatus === 'cancelled' && (
        <div data-testid="billing-cancel-banner"
          className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <AlertCircle className="h-5 w-5 text-amber-600 shrink-0" />
          <p className="text-sm text-amber-800">Ödeme iptal edildi. Aşağıdan tekrar deneyebilirsiniz.</p>
        </div>
      )}

      {/* Subscription Status Panel */}
      {subscription && (
        <>
          <SubscriptionStatusPanel
            sub={subscription}
            onCancel={() => setShowCancelModal(true)}
            onReactivate={handleReactivate}
            actionLoading={actionLoading}
          />
          {/* Stripe Customer Portal button — only for paid Stripe subs */}
          {subscription.stripe_subscription_id && (
            <div className="flex justify-end -mt-2">
              <button
                onClick={async () => {
                  try {
                    const res = await axios.post(`${API}/billing/portal`,
                      { return_url: window.location.href },
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    window.location.href = res.data.url;
                  } catch (e) {
                    showToast(e?.response?.data?.detail || 'Portal açılamadı.', 'error');
                  }
                }}
                className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 underline"
                data-testid="stripe-portal-btn"
              >
                <CreditCard className="h-3 w-3" />
                Kart bilgilerini güncelle / faturalama geçmişi
              </button>
            </div>
          )}
        </>
      )}

      {/* Usage Panel */}
      <UsagePanel token={token} onUpgradeClick={() => {
        // Scroll to plan cards section
        document.getElementById('billing-plans')?.scrollIntoView({ behavior: 'smooth' });
      }} />

      {/* Interval toggle */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1 w-fit">
          {['month', 'year'].map(v => (
            <button
              key={v}
              data-testid={`interval-${v}`}
              onClick={() => setIntervalState(v)}
              className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${
                interval === v ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {v === 'month' ? 'Aylık' : 'Yıllık'}
              {v === 'year' && <span className="ml-1.5 text-xs font-bold text-green-600 bg-green-100 px-1.5 py-0.5 rounded-full">-17%</span>}
            </button>
          ))}
        </div>
        {interval === 'year' && (
          <p className="text-xs text-green-600 font-semibold bg-green-50 px-3 py-1.5 rounded-full border border-green-200">
            2 ay ücretsiz = yılda 2 aylık kazanç
          </p>
        )}
      </div>

      {/* Plan cards */}
      <div id="billing-plans" className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 items-start">
        {plans.map(plan => {
          const price = interval === 'year' ? Math.round(plan.price_month * 10) : plan.price_month;
          const isCurrent = currentPlanCode === plan.code;
          const isFree = plan.price_month === 0;
          const isUpgrade = !isCurrent && plan.price_month > (subscription?.price_month || 0);
          const isDowngrade = !isCurrent && plan.price_month < (subscription?.price_month || 0) && !isFree;
          const isPopular = !isCurrent && !isFree && plan.sort_order === 2;
          const planColor = plan.highlight_color || '#6b7280';

          const tierIcon = plan.sort_order === 1 ? Zap : plan.sort_order === 2 ? Star : Crown;
          const TierIcon = tierIcon;

          return (
            <div
              key={plan.code}
              data-testid={`plan-card-${plan.code}`}
              className={`relative rounded-2xl border-2 transition-all duration-200 overflow-hidden
                ${isCurrent
                  ? 'border-orange-400 shadow-xl shadow-orange-100'
                  : isPopular
                  ? 'border-2 shadow-xl -translate-y-1'
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-md hover:-translate-y-0.5'
                }`}
              style={isPopular ? { borderColor: planColor } : {}}
            >
              {/* Gradient top section */}
              <div
                className="px-6 pt-6 pb-5"
                style={{
                  background: isCurrent
                    ? 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)'
                    : isPopular
                    ? `linear-gradient(135deg, ${planColor}18 0%, ${planColor}08 100%)`
                    : 'white'
                }}
              >
                {/* Badges */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${planColor}22` }}>
                      <TierIcon className="h-5 w-5" style={{ color: planColor }} />
                    </div>
                    <h3 className="text-base font-black text-gray-900">{plan.name}</h3>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {isCurrent && (
                      <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-orange-500 text-white shadow-sm">✓ Aktif</span>
                    )}
                    {isPopular && (
                      <span className="text-[10px] font-bold px-2.5 py-1 rounded-full text-white shadow-sm whitespace-nowrap" style={{ backgroundColor: planColor }}>⭐ En Popüler</span>
                    )}
                    {isUpgrade && !isPopular && (
                      <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 whitespace-nowrap">↑ Yükselt</span>
                    )}
                  </div>
                </div>

                {plan.description && <p className="text-xs text-gray-500 mb-4">{plan.description}</p>}

                {/* Price */}
                <div className="mb-1">
                  {isFree ? (
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-black text-gray-900">Ücretsiz</span>
                    </div>
                  ) : (
                    <div className="flex items-baseline gap-1">
                      <span className="text-xs font-semibold text-gray-400 mb-1 self-start mt-2">{plan.currency === 'EUR' ? '€' : '$'}</span>
                      <span className="text-5xl font-black text-gray-900 leading-none">{price}</span>
                      <span className="text-sm text-gray-400 ml-1">/{interval === 'month' ? 'ay' : 'yıl'}</span>
                    </div>
                  )}
                  {interval === 'year' && !isFree && (
                    <p className="text-xs text-green-600 mt-1 font-medium">
                      Normalde {plan.currency === 'EUR' ? '€' : '$'}{plan.price_month * 12}/yıl → Tasarruf: {plan.currency === 'EUR' ? '€' : '$'}{Math.round(plan.price_month * 2)}
                    </p>
                  )}
                </div>
              </div>

              {/* Features */}
              <div className="px-6 py-4 bg-white border-t border-gray-100">
                {plan.features && typeof plan.features === 'object' && !Array.isArray(plan.features) && (
                  <ul className="space-y-2.5 mb-5">
                    {Object.entries(plan.features).slice(0, 6).map(([key, val]) => (
                      <FeatureRow key={key} featureKey={key} value={val} />
                    ))}
                  </ul>
                )}

                {/* CTA Button */}
                <button
                  data-testid={`plan-btn-${plan.code}`}
                  onClick={() => !isCurrent && handleSubscribe(plan)}
                  disabled={isCurrent || checkoutLoading === plan.code}
                  className={`w-full py-3.5 px-4 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all ${
                    isCurrent
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : isDowngrade
                      ? 'bg-gray-800 hover:bg-gray-900 text-white'
                      : 'text-white shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0'
                  }`}
                  style={!isCurrent && !isDowngrade ? { backgroundColor: planColor } : {}}
                >
                  {checkoutLoading === plan.code ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : isCurrent ? (
                    '✓ Aktif Plan'
                  ) : isFree ? (
                    <><ArrowRight className="h-4 w-4" /> Ücretsiz Plana Geç</>
                  ) : subscription?.stripe_subscription_id ? (
                    <>
                      {isUpgrade ? <Rocket className="h-4 w-4" /> : <TrendingUp className="h-4 w-4" />}
                      {isDowngrade ? 'Bu Plana Düşür' : 'Bu Plana Yükselt'}
                    </>
                  ) : (
                    <><Zap className="h-4 w-4" /> Başla — {isFree ? 'Ücretsiz' : 'Stripe ile Öde'}</>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Feature Comparison Table */}
      {plans.length > 1 && (
        <div className="overflow-x-auto rounded-2xl border border-gray-200 shadow-sm" data-testid="plan-comparison-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left py-4 px-5 font-semibold text-gray-600 w-52">Özellik</th>
                {plans.map(plan => (
                  <th key={plan.code} className={`py-4 px-4 text-center font-bold text-xs ${
                    currentPlanCode === plan.code ? 'text-orange-600' : 'text-gray-700'
                  }`}>
                    <div className="flex flex-col items-center gap-1">
                      <span>{plan.name}</span>
                      {currentPlanCode === plan.code && (
                        <span className="text-[10px] bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full font-semibold">Aktif</span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {Object.keys(FEATURE_LABELS).map(featureKey => (
                <tr key={featureKey} className="hover:bg-gray-50 transition-colors">
                  <td className="py-3 px-5 text-gray-600 text-xs font-medium">{FEATURE_LABELS[featureKey]}</td>
                  {plans.map(plan => {
                    const val = plan.features?.[featureKey];
                    const isActive = currentPlanCode === plan.code;
                    return (
                      <td key={plan.code} className={`py-3 px-4 text-center text-xs ${isActive ? 'bg-orange-50/50' : ''}`}>
                        {val === true ? (
                          <span className="inline-block text-green-500 font-bold">✓</span>
                        ) : val === false ? (
                          <span className="inline-block text-gray-300">—</span>
                        ) : val === -1 ? (
                          <span className="text-purple-600 font-semibold">∞</span>
                        ) : val !== undefined ? (
                          <span className="text-gray-700 font-medium">{val}</span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Info footer */}
      <div className="bg-gray-50 rounded-xl p-5 space-y-2">
        <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <Shield className="h-4 w-4 text-gray-400" />
          Abonelik Politikası
        </h4>
        <ul className="space-y-1.5 text-xs text-gray-500">
          <li>• Aylık abonelikler her ay otomatik yenilenir ve kartınızdan çekilir.</li>
          <li>• İptal ederseniz mevcut dönem sonuna kadar erişiminiz devam eder.</li>
          <li>• Yükseltme anlık olarak uygulanır ve kalan günler için kısmi iade yapılır.</li>
          <li>• Tüm ödemeler Stripe altyapısıyla güvenli şekilde işlenir.</li>
        </ul>
      </div>

      <p className="text-xs text-gray-400 text-center">
        Fiyatlar EUR cinsindendir. Stripe ile güvenli ödeme.
      </p>

      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <CancelModal
          sub={subscription}
          onConfirm={handleCancel}
          onClose={() => setShowCancelModal(false)}
          loading={actionLoading}
        />
      )}
    </div>
  );
}
